import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { WorkspaceShell } from "./WorkspaceShell";
import { toast } from "sonner";
import {
  Plus, Filter, Download, ExternalLink, AlertTriangle, Ban, Clock,
  CheckCircle2, X, Edit3, Trash2, ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Operations Matrix — live RACI task ownership board.
 * All 9 workstreams · 26 seeded tasks · filter by owner/workstream/status.
 * Feeds Launch Readiness (blocked → critical, at_risk/overdue → attention).
 */
export default function AdminOperationsMatrix() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ workstream: "", status: "", owner: "" });
  const [editing, setEditing] = useState(null);   // task being edited or "new"

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/login"); return; }
    api.get("/admin/operations/meta").then(({ data }) => setMeta(data));
    reload();
  }, [user, authLoading, nav]);

  const reload = () => {
    setLoading(true);
    return api.get("/admin/operations/matrix").then(({ data }) => {
      setTasks(data.tasks || []);
      setRollup(data.rollup || null);
    }).finally(() => setLoading(false));
  };

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filters.workstream && t.workstream !== filters.workstream) return false;
      if (filters.status && t.status !== filters.status) return false;
      if (filters.owner) {
        const all = [t.responsible, t.accountable, ...(t.consulted || []), ...(t.informed || [])];
        if (!all.includes(filters.owner)) return false;
      }
      return true;
    });
  }, [tasks, filters]);

  const grouped = useMemo(() => {
    if (!meta) return [];
    const map = {};
    filtered.forEach((t) => {
      if (!map[t.workstream]) map[t.workstream] = [];
      map[t.workstream].push(t);
    });
    return meta.workstreams
      .map((ws) => ({ ...ws, tasks: (map[ws.key] || []).sort((a, b) => (a.order || 0) - (b.order || 0)) }))
      .filter((ws) => ws.tasks.length > 0);
  }, [filtered, meta]);

  const download = async () => {
    const tok = localStorage.getItem("summit_token");
    const url = `${BACKEND_URL}/api/admin/operations/matrix.csv`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "operations-matrix.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const saveTask = async (payload) => {
    try {
      if (editing === "new") {
        await api.post("/admin/operations/matrix", payload);
        toast.success("Task added");
      } else {
        await api.put(`/admin/operations/matrix/${editing.id}`, payload);
        toast.success("Task updated");
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/admin/operations/matrix/${id}`);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  const updateInline = async (task, patch) => {
    try {
      const { data } = await api.put(`/admin/operations/matrix/${task.id}`, patch);
      setTasks((ts) => ts.map((t) => t.id === task.id ? data.task : t));
      // Refresh rollup counters
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <>
      <WorkspaceShell
        title="Operations Matrix"
        subtitle="Live RACI ownership across nine workstreams. Update status, ownership, and due dates — Launch Readiness reflects it instantly."
        dataTestId="ops-matrix-title"
        headerActions={
          <>
            <button onClick={() => setEditing("new")}
                    className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white"
                    data-testid="ops-new-task">
              <Plus size={12} /> New Task
            </button>
            <button onClick={download}
                    className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white"
                    data-testid="ops-export-csv">
              <Download size={12} /> Export CSV
            </button>
          </>
        }
        tabs={[{
          value: "matrix", label: "Matrix", render: () => (
            <div data-testid="ops-matrix">
              {/* Rollup */}
              {rollup && meta && <Rollup rollup={rollup} meta={meta} />}

              {/* Filters */}
              {meta && <FilterBar meta={meta} filters={filters} setFilters={setFilters} />}

              {/* Body */}
              {loading ? (
                <div className="text-sm text-zinc-500 py-8">Loading…</div>
              ) : grouped.length === 0 ? (
                <div className="text-sm text-zinc-500 py-8">No tasks match the current filters.</div>
              ) : (
                <div className="mt-6 grid gap-8">
                  {grouped.map((ws) => (
                    <WorkstreamSection
                      key={ws.key} ws={ws} meta={meta}
                      onEdit={setEditing}
                      onDelete={deleteTask}
                      onInline={updateInline}
                    />
                  ))}
                </div>
              )}
            </div>
          ),
        }]}
      />
      {editing && meta && (
        <TaskEditor
          task={editing === "new" ? null : editing}
          meta={meta}
          onClose={() => setEditing(null)}
          onSave={saveTask}
        />
      )}
    </>
  );
}

/* -------- Rollup counters -------- */
function Rollup({ rollup, meta }) {
  const items = [
    { key: "total",       label: "Total Tasks",  value: rollup.total,       accent: "neutral" },
    { key: "in_progress", label: "In Progress",  value: rollup.in_progress, accent: "neutral" },
    { key: "complete",    label: "Complete",     value: rollup.complete,    accent: "good" },
    { key: "at_risk",     label: "At Risk",      value: rollup.at_risk,     accent: rollup.at_risk > 0 ? "attention" : "neutral" },
    { key: "blocked",     label: "Blocked",      value: rollup.blocked,     accent: rollup.blocked > 0 ? "critical" : "neutral" },
    { key: "overdue",     label: "Overdue",      value: rollup.overdue,     accent: rollup.overdue > 0 ? "attention" : "neutral" },
  ];
  const cls = (accent) => (
    accent === "critical" ? "text-red-600 border-red-600" :
    accent === "attention" ? "text-amber-700 border-amber-500" :
    accent === "good" ? "text-emerald-700 border-emerald-500" :
    "text-black border-zinc-200"
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-100 border border-zinc-100" data-testid="ops-rollup">
      {items.map((it) => (
        <div key={it.key} className={`bg-white p-5 border-t-2 ${cls(it.accent)}`} data-testid={`ops-rollup-${it.key}`}>
          <div className="serif text-3xl tabular-nums">{it.value ?? 0}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

/* -------- Filter bar -------- */
function FilterBar({ meta, filters, setFilters }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clear = () => setFilters({ workstream: "", status: "", owner: "" });
  const active = filters.workstream || filters.status || filters.owner;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2 border border-zinc-200 p-3" data-testid="ops-filters">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-1 pr-2">
        <Filter size={11} /> Filter
      </div>
      <FilterSelect testId="ops-filter-workstream" value={filters.workstream}
        onChange={(v) => set("workstream", v)}
        placeholder="Workstream" placeholderAll="All Workstreams"
        options={meta.workstreams.map((w) => ({ value: w.key, label: w.label }))} />
      <FilterSelect testId="ops-filter-owner" value={filters.owner}
        onChange={(v) => set("owner", v)}
        placeholder="Owner" placeholderAll="All Owners"
        options={meta.team.map((m) => ({ value: m.id, label: m.name }))} />
      <FilterSelect testId="ops-filter-status" value={filters.status}
        onChange={(v) => set("status", v)}
        placeholder="Status" placeholderAll="All Statuses"
        options={meta.statuses.map((s) => ({ value: s.key, label: s.label }))} />
      {active && (
        <button onClick={clear}
                className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1 ml-1"
                data-testid="ops-filter-clear">
          <X size={11} /> Clear
        </button>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, placeholderAll, options, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="h-8 px-3 text-[11px] uppercase tracking-[0.18em] border border-zinc-200 hover:border-black bg-white outline-none"
            data-testid={testId}>
      <option value="">{placeholderAll || `All ${placeholder}`}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/* -------- Workstream section -------- */
function WorkstreamSection({ ws, meta, onEdit, onDelete, onInline }) {
  const total = ws.tasks.length;
  const done = ws.tasks.filter((t) => t.status === "complete").length;
  return (
    <section data-testid={`ops-ws-${ws.key}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="overline">Workstream</div>
          <h2 className="serif text-2xl mt-1">{ws.label}</h2>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 tabular-nums">
          {done} / {total} complete
        </div>
      </div>
      <div className="border border-black bg-white">
        <div className="hidden md:grid grid-cols-[3fr_1fr_1fr_1fr_1fr_auto] gap-4 bg-zinc-50 border-b border-zinc-200 px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <div>Deliverable / Task</div>
          <div>Responsible</div>
          <div>Accountable</div>
          <div>Due</div>
          <div>Status</div>
          <div></div>
        </div>
        <div className="divide-y divide-zinc-100">
          {ws.tasks.map((t) => (
            <TaskRow key={t.id} task={t} meta={meta}
              onEdit={onEdit} onDelete={onDelete} onInline={onInline} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------- Task row -------- */
function TaskRow({ task, meta, onEdit, onDelete, onInline }) {
  const nameOf = (id) => meta.team.find((m) => m.id === id)?.name || "—";
  const rw = meta.related_workspaces.find((w) => w.slug === task.related_workspace);
  const overdue = task.overdue;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr_1fr_1fr_1fr_auto] gap-4 px-5 py-4 items-center hover:bg-zinc-50 group" data-testid={`ops-task-${task.id}`}>
      <div className="min-w-0">
        <div className="text-[15px] leading-snug text-black">{task.deliverable}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
          {(task.consulted?.length > 0) && (
            <span data-testid={`ops-task-${task.id}-consulted`}>
              <span className="text-[9px] uppercase tracking-[0.22em] mr-1">C</span>
              {task.consulted.map((c) => nameOf(c)).join(" · ")}
            </span>
          )}
          {(task.informed?.length > 0) && (
            <span data-testid={`ops-task-${task.id}-informed`}>
              <span className="text-[9px] uppercase tracking-[0.22em] mr-1">I</span>
              {task.informed.map((c) => nameOf(c)).join(" · ")}
            </span>
          )}
          {rw && (
            <Link to={rw.href} className="text-[#A39171] hover:text-black inline-flex items-center gap-1"
                  data-testid={`ops-task-${task.id}-related`}>
              {rw.label} <ExternalLink size={10} />
            </Link>
          )}
        </div>
        {task.notes && <div className="mt-1 text-[12px] text-zinc-500 italic">{task.notes}</div>}
      </div>

      <div className="text-[13px]" data-testid={`ops-task-${task.id}-r`}>{nameOf(task.responsible)}</div>
      <div className="text-[13px]" data-testid={`ops-task-${task.id}-a`}>{nameOf(task.accountable)}</div>

      <div className={`text-[13px] tabular-nums ${overdue ? "text-red-600 font-medium" : "text-zinc-700"}`}
           data-testid={`ops-task-${task.id}-due`}>
        {task.due_date || "—"}
        {overdue && <span className="ml-1 text-[10px] uppercase tracking-[0.18em] text-red-600">Overdue</span>}
      </div>

      <div>
        <StatusPill task={task} meta={meta} onChange={(status) => onInline(task, { status })} />
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onEdit(task)} className="p-1.5 hover:bg-zinc-100 text-zinc-500 hover:text-black" title="Edit" data-testid={`ops-task-${task.id}-edit`}>
          <Edit3 size={13} />
        </button>
        <button onClick={() => onDelete(task.id)} className="p-1.5 hover:bg-red-50 text-zinc-400 hover:text-red-600" title="Delete" data-testid={`ops-task-${task.id}-delete`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  not_started: "border-zinc-300 text-zinc-600 bg-white",
  in_progress: "border-blue-500 text-blue-700 bg-blue-50",
  at_risk:     "border-amber-500 text-amber-800 bg-amber-50",
  blocked:     "border-red-500 text-red-700 bg-red-50",
  complete:    "border-emerald-500 text-emerald-700 bg-emerald-50",
};

function StatusPill({ task, meta, onChange }) {
  return (
    <select value={task.status} onChange={(e) => onChange(e.target.value)}
            className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 outline-none cursor-pointer ${STATUS_STYLE[task.status] || STATUS_STYLE.not_started}`}
            data-testid={`ops-task-${task.id}-status`}>
      {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  );
}

/* -------- Editor modal -------- */
function TaskEditor({ task, meta, onClose, onSave }) {
  const isNew = !task;
  const [form, setForm] = useState(() => ({
    workstream: task?.workstream || meta.workstreams[0].key,
    deliverable: task?.deliverable || "",
    responsible: task?.responsible || meta.team[0].id,
    accountable: task?.accountable || meta.team[0].id,
    consulted: task?.consulted || [],
    informed: task?.informed || [],
    due_date: task?.due_date || "",
    status: task?.status || "not_started",
    notes: task?.notes || "",
    related_workspace: task?.related_workspace || "",
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggle = (field, id) => {
    setForm((f) => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.deliverable.trim()) return toast.error("Deliverable is required.");
    const payload = { ...form };
    if (!payload.due_date) payload.due_date = null;
    if (!payload.related_workspace) payload.related_workspace = null;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center overflow-auto py-8 px-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white max-w-3xl w-full border border-black shadow-2xl" data-testid="ops-editor">
        <div className="flex items-center justify-between bg-black text-white px-5 py-3">
          <div className="text-[11px] uppercase tracking-[0.22em]">{isNew ? "New Task" : "Edit Task"}</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/10" data-testid="ops-editor-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-6 grid gap-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Workstream *">
              <select value={form.workstream} onChange={(e) => set("workstream", e.target.value)}
                      className="w-full border border-zinc-300 h-10 px-3 text-sm outline-none focus:border-black" data-testid="ops-f-workstream">
                {meta.workstreams.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                      className="w-full border border-zinc-300 h-10 px-3 text-sm outline-none focus:border-black" data-testid="ops-f-status">
                {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Deliverable / Task *">
            <Textarea value={form.deliverable} onChange={(e) => set("deliverable", e.target.value)}
                      rows={2} placeholder="What is being delivered?" data-testid="ops-f-deliverable" />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Responsible (R) *">
              <TeamSelect team={meta.team} value={form.responsible} onChange={(v) => set("responsible", v)} testId="ops-f-responsible" />
            </Field>
            <Field label="Accountable (A) *">
              <TeamSelect team={meta.team} value={form.accountable} onChange={(v) => set("accountable", v)} testId="ops-f-accountable" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Consulted (C)">
              <TeamMulti team={meta.team} value={form.consulted} onToggle={(id) => toggle("consulted", id)} testId="ops-f-consulted" />
            </Field>
            <Field label="Informed (I)">
              <TeamMulti team={meta.team} value={form.informed} onToggle={(id) => toggle("informed", id)} testId="ops-f-informed" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Due Date">
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="ops-f-due" />
            </Field>
            <Field label="Related Workspace">
              <select value={form.related_workspace} onChange={(e) => set("related_workspace", e.target.value)}
                      className="w-full border border-zinc-300 h-10 px-3 text-sm outline-none focus:border-black" data-testid="ops-f-related">
                <option value="">None</option>
                {meta.related_workspaces.map((w) => <option key={w.slug} value={w.slug}>{w.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Notes">
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="ops-f-notes" />
          </Field>
        </div>

        <div className="border-t border-zinc-200 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 text-[11px] uppercase tracking-[0.22em] border border-zinc-200 hover:border-black">
            Cancel
          </button>
          <button type="submit"
                  className="btn-black h-10 px-5 text-[11px]"
                  data-testid="ops-editor-save">
            {isNew ? "Add Task" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="overline mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function TeamSelect({ team, value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full border border-zinc-300 h-10 px-3 text-sm outline-none focus:border-black" data-testid={testId}>
      {team.map((m) => <option key={m.id} value={m.id}>{`${m.name} · ${m.role}`}</option>)}
    </select>
  );
}

function TeamMulti({ team, value, onToggle, testId }) {
  return (
    <div className="border border-zinc-300 max-h-40 overflow-auto p-2 grid gap-1" data-testid={testId}>
      {team.map((m) => (
        <label key={m.id} className="flex items-center gap-2 text-sm py-1 px-1 hover:bg-zinc-50 cursor-pointer">
          <input type="checkbox" checked={value.includes(m.id)} onChange={() => onToggle(m.id)}
                 data-testid={`${testId}-${m.id}`} />
          <span>{m.name}</span>
          <span className="text-[10px] text-zinc-400 ml-auto">{m.role}</span>
        </label>
      ))}
    </div>
  );
}
